import { useEffect, useState } from "react"
import {
  generateMonthlyReport,
  getMonthlyReportStatus,
  listMonthlyReports,
} from "../../Http/Index"

export interface ReportSnapshot {
  _id: string
  reportMonth: string
  reportYear: number
  quarter: string
  monthLabel: string
  status: "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED"
  ticketCount: number
  errorMessage: string | null
  generatedAt: string
}

export interface RefEntry {
  _id: string
  companyName: string
  client: string
  category: string
  isActivePartnership: boolean
}

export const useMonthlyReport = () => {
  const [snapshots, setSnapshots] = useState<ReportSnapshot[]>([])
  const [selectedMonth, setSelectedMonth] = useState<string>("")
  const [isGenerating, setIsGenerating] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string>("")

  useEffect(() => {
    fetchSnapshots()
  }, [])

  const fetchSnapshots = async () => {
    setIsLoading(true)
    try {
      const res = await listMonthlyReports(1, 24)
      const completed: ReportSnapshot[] = (res.data?.data || []).filter(
        (s: ReportSnapshot) => s.status === "COMPLETED"
      )
      setSnapshots(completed)
      if (completed.length > 0) {
        setSelectedMonth(completed[0].reportMonth)
      }
    } catch {
      setError("Failed to load report list")
    } finally {
      setIsLoading(false)
    }
  }

  const triggerGenerate = async (month: string) => {
    setIsGenerating(true)
    setError("")
    try {
      await generateMonthlyReport(month)
      const poll = setInterval(async () => {
        const res = await getMonthlyReportStatus(month)
        const status = res.data?.data?.status
        if (status === "COMPLETED") {
          clearInterval(poll)
          setIsGenerating(false)
          fetchSnapshots()
        } else if (status === "FAILED") {
          clearInterval(poll)
          setIsGenerating(false)
          setError(res.data?.data?.errorMessage || "Report generation failed")
        }
      }, 4000)
    } catch {
      setIsGenerating(false)
      setError("Failed to trigger report generation")
    }
  }

  return {
    snapshots,
    selectedMonth,
    setSelectedMonth,
    isGenerating,
    isLoading,
    error,
    triggerGenerate,
    fetchSnapshots,
  }
}
